import {createContext, Dispatch, ReactNode, useContext, useReducer, useRef} from 'react';
import HeadsetService from 'softphone-vendor-headsets';
import {
    HeadsetInputContextState,
    HeadsetInputReducerAction,
    headsetReducer,
    initialState
} from './reducer';
import { HeadsetStatus, OperationEvent, useConnectedHeadsets } from './hooks';
import {useTypedDispatch} from "../../redux/hooks";
import {addOneToast} from "../../redux/slices";

interface Props {
    reducer: Dispatch<HeadsetInputReducerAction>;
    state: HeadsetInputContextState;
    handleOperation: (operation: OperationEvent) => void;
    requestDevice: () => void;
    connectDevice: (device: HIDDevice) => Promise<void>;
	disconnectCurrentDevice: () => Promise<void>;
    headsetStatusRef: React.MutableRefObject<HeadsetStatus>;
}

const HeadsetContext = createContext<Props | null>(null);

export const useHeadsetContext = () => {
    const context = useContext(HeadsetContext);

    if (!context) {
        throw new Error('Component requiring headset context rendered without context');
    }

    const headsetState: HeadsetInputContextState = context.state;
    const useHeadsetAction: (val: HeadsetInputReducerAction) => void = context.reducer;

    return {
        ...headsetState,
        headsetStatusRef: context.headsetStatusRef,
        handleOperation: context.handleOperation,
        requestDevice: context.requestDevice,
        connectDevice: context.connectDevice,
	    disconnectCurrentDevice: context.disconnectCurrentDevice,
        useHeadsetAction
    };
};

interface ProviderProps {
    children: ReactNode;
}

const HeadsetProvider: React.FC<ProviderProps> = ({ children }) => {
	const dispatch = useTypedDispatch();

    const [state, reducer] = useReducer(headsetReducer, { ...initialState });
	const headsetStatusRef = useRef<HeadsetStatus>({ connected: false, muted: false, hold: false, activeCallId: null });

	const headsetService = HeadsetService.getInstance({
        appName: 'Nebula Cloud Ltd',
        logger: {
            info: (msg: any) => {
                console.info(`[Headset Service] INFO ${msg}`);
            },
            error: (error: any) => {
				if (error instanceof Error) {
					dispatch(addOneToast({
						type: 'error',
						title: 'Headset Service Error',
						content: error.toString(),
					}))
				}
                console.error(`[Headset Service] ERROR ${error}`);
            },
            debug: (msg: any) => {
                console.debug(`[Headset Service] DEBUG ${msg}`);
            },
            warn: (msg: any) => {
                console.error(`[Headset Service] WARN ${msg}`);
            }
        }
    });

    const { requestDevice, handleOperation, connectDevice, disconnectCurrentDevice } = useConnectedHeadsets({
        reducer,
        headsetService,
	    headsetStatusRef
    });

    return (
        <HeadsetContext.Provider
            value={{ reducer, state, requestDevice, handleOperation, connectDevice, headsetStatusRef, disconnectCurrentDevice }}
        >
            {children}
        </HeadsetContext.Provider>
    );
};

export default HeadsetProvider;
